import Markdown from '@/components/atoms/Markdown/Markdown';
import CustomButton from '@/components/atoms/CustomButton';
import MarkdownEditor from '@/components/organisms/MarkdownEditor/MarkdownEditor';
import { articleFieldKey, TArticleFieldKind } from '@/modules/content/creation/model/articleValidation';
import { CONTENT_ARTICLE_SECTION_TEXT_MAX_LENGTH, CONTENT_TITLE_MAX_LENGTH } from '@/constants/contentLimits';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { Box, Collapse, Divider, HStack, Input, Text, VStack } from '@chakra-ui/react';
import React, { useEffect, useRef, useState } from 'react';
import ImagePicker from '@/modules/content/common/ImagePicker';
import { makeEmptySection, makeEmptySubsection, TArticleSectionDraft, TArticleSubsectionDraft } from '@/modules/content/creation/model/types';

// 'edit' = author titles + markdown text + structure; 'arrange' = read-only text,
// manage images, and add/remove sections. Both share the collapsible layout.
type Mode = 'edit' | 'arrange';

type Props = {
  mode: Mode;
  sections: TArticleSectionDraft[];
  onChange: (sections: TArticleSectionDraft[]) => void;
  // Whole-article problems (no sections at all, over the total length).
  error?: string;
  // Per-field problems, keyed by `articleFieldKey`, so each lands under its own input.
  fieldErrors?: Partial<Record<string, string>>;
};

const sectionBoxProps = {
  backgroundColor: 'white',
  border: '1px solid',
  borderColor: 'background.separatorBlue',
  borderRadius: '12px',
  padding: '16px 18px',
} as const;

const RemoveLink = ({ label, onClick }: { label: string; onClick: () => void }) => (
  <Text
    fontSize="11px"
    fontWeight={700}
    color="extra.red"
    textTransform="uppercase"
    letterSpacing="0.4px"
    cursor="pointer"
    _hover={{ textDecoration: 'underline' }}
    onClick={onClick}
  >
    {label}
  </Text>
);

const CollapseHeader = ({
  label,
  fontSize,
  isOpen,
  onToggle,
  removeLabel,
  onRemove,
}: {
  label: string;
  fontSize: string;
  isOpen: boolean;
  onToggle: () => void;
  removeLabel: string;
  onRemove: () => void;
}) => (
  <HStack justify="space-between" align="center">
    <HStack cursor="pointer" gap="8px" onClick={onToggle}>
      {isOpen ? <ChevronUpIcon boxSize={5} color="text.darkBlue" /> : <ChevronDownIcon boxSize={5} color="text.darkBlue" />}
      <Text fontSize={fontSize} fontWeight={700} color="text.darkBlue">
        {label}
      </Text>
    </HStack>
    <RemoveLink label={removeLabel} onClick={onRemove} />
  </HStack>
);

// Read-only title box (arrange mode).
const ReadonlyTitle = ({ value }: { value: string }) => (
  <Box {...sectionBoxProps}>
    <Text color={value ? 'text.darkBlue' : 'text.mediumGray'} fontWeight={600}>
      {value || 'Untitled'}
    </Text>
  </Box>
);

// Read-only rendered text box (arrange mode).
const ReadonlyText = ({ value }: { value: string }) => (
  <Box {...sectionBoxProps}>
    <Markdown>{value || '_No content_'}</Markdown>
  </Box>
);

// Everything the creator needs to know about one block of text, on one line: what is wrong
// with it on the left, how much of the limit is used on the right. The backend caps every
// section and subsection independently, so the limit is worth surfacing while typing rather
// than at submit — and the message belongs beside it, not a line further from the field.
const TextFooter = ({ value, error }: { value: string; error?: string }) => {
  const isOverLimit = value.length > CONTENT_ARTICLE_SECTION_TEXT_MAX_LENGTH;

  return (
    <HStack align="flex-start" justify="space-between" gap="12px">
      <Text color="extra.red" fontSize="12px" fontWeight={600}>
        {error}
      </Text>
      <Text fontSize="12px" flexShrink={0} color={isOverLimit ? 'extra.red' : 'text.mediumGray'}>
        {value.length} / {CONTENT_ARTICLE_SECTION_TEXT_MAX_LENGTH}
      </Text>
    </HStack>
  );
};

// A field and its message travel together: a tight 6px gap so the error reads as belonging
// to the input above it rather than floating in the section's 12px rhythm.
const Field = ({ error, children }: { error?: string; children: React.ReactNode }) => (
  <VStack align="stretch" gap="6px">
    {children}
    {error && (
      <Text color="extra.red" fontSize="12px" fontWeight={600}>
        {error}
      </Text>
    )}
  </VStack>
);

// Outline for whichever input is at fault. The message says what is wrong; the border says
// where — worth having when a long article puts several inputs on screen at once.
const invalidBoxProps = (isInvalid: boolean) =>
  isInvalid ? { borderColor: 'extra.red', boxShadow: '0 0 0 1px var(--chakra-colors-extra-red)' } : {};

// The markdown editor has no border — it draws a 1px box-shadow ring — so bordering the
// wrapper around it paints a stray line rather than outlining the field.
//
// Replaced with an *inset* ring rather than recolouring the library's outer one: an outer
// ring sits a pixel outside the editor, where an ancestor's overflow clips the left and
// right edges and leaves the outline looking cut off at the sides. Drawn inside, it always
// closes, and it still follows the editor's 8px radius.
const invalidEditorSx = (isInvalid: boolean) =>
  isInvalid
    ? { '& .w-md-editor': { boxShadow: 'inset 0 0 0 1px var(--chakra-colors-extra-red)' } }
    : undefined;

const ArticleSectionsEditor = ({ mode, sections, onChange, error, fieldErrors = {} }: Props) => {
  const isEdit = mode === 'edit';
  // Collapsed ids (default everything expanded).
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  // A block that has an error cannot stay collapsed — otherwise the wizard refuses to move
  // on and the reason is hidden inside a folded block the creator has no reason to open.
  const hasError = (kind: TArticleFieldKind, id: string) =>
    Boolean(fieldErrors[articleFieldKey(kind, id, 'title')] || fieldErrors[articleFieldKey(kind, id, 'text')]);

  // A section must also open for a problem in one of its subsections. Forcing the subsection
  // open is not enough on its own: it renders inside the section's own <Collapse>, so a
  // perfectly valid section would keep the error folded away inside it.
  const hasErrorWithin = (section: TArticleSectionDraft) =>
    hasError('section', section._id) || section.subsections.some((sub) => hasError('subsection', sub._id));

  const isSubsectionOpen = (id: string) => hasError('subsection', id) || !collapsed.has(id);

  // Bring the first problem into view. The article can be long enough that an error below
  // the fold reads as the wizard refusing to advance for no reason.
  const blockRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const firstErrorId = sections
    .flatMap((section) => [
      { kind: 'section' as const, id: section._id },
      ...section.subsections.map((sub) => ({ kind: 'subsection' as const, id: sub._id })),
    ])
    .find(({ kind, id }) => hasError(kind, id))?.id;

  useEffect(() => {
    if (!firstErrorId) return;
    blockRefs.current[firstErrorId]?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, [firstErrorId]);
  const toggle = (id: string) =>
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const updateSection = (id: string, patch: Partial<TArticleSectionDraft>) =>
    onChange(sections.map((s) => (s._id === id ? { ...s, ...patch } : s)));
  const removeSection = (id: string) => onChange(sections.filter((s) => s._id !== id));
  const addSection = () => onChange([...sections, makeEmptySection()]);

  const updateSubsection = (sectionId: string, subId: string, patch: Partial<TArticleSubsectionDraft>) =>
    onChange(
      sections.map((s) =>
        s._id === sectionId ? { ...s, subsections: s.subsections.map((sub) => (sub._id === subId ? { ...sub, ...patch } : sub)) } : s,
      ),
    );
  const addSubsection = (sectionId: string) =>
    onChange(sections.map((s) => (s._id === sectionId ? { ...s, subsections: [...s.subsections, makeEmptySubsection()] } : s)));
  const removeSubsection = (sectionId: string, subId: string) =>
    onChange(sections.map((s) => (s._id === sectionId ? { ...s, subsections: s.subsections.filter((sub) => sub._id !== subId) } : s)));

  const renderSubsection = (sectionId: string, sub: TArticleSubsectionDraft, subIndex: number) => (
    <VStack
      key={sub._id}
      ref={(el) => {
        blockRefs.current[sub._id] = el;
      }}
      align="stretch"
      gap="10px"
      marginTop="6px"
    >
      <CollapseHeader
        label={`Sub Section ${subIndex + 1}`}
        fontSize="15px"
        isOpen={isSubsectionOpen(sub._id)}
        onToggle={() => toggle(sub._id)}
        removeLabel="Remove sub-section"
        onRemove={() => removeSubsection(sectionId, sub._id)}
      />
      <Collapse in={isSubsectionOpen(sub._id)} animateOpacity>
        <VStack align="stretch" gap="10px">
          <Field error={fieldErrors[articleFieldKey('subsection', sub._id, 'title')]}>
            {isEdit ? (
              <Input
                {...sectionBoxProps}
                {...invalidBoxProps(Boolean(fieldErrors[articleFieldKey('subsection', sub._id, 'title')]))}
                placeholder="Sub-section title"
                maxLength={CONTENT_TITLE_MAX_LENGTH}
                value={sub.title}
                onChange={(e) => updateSubsection(sectionId, sub._id, { title: e.target.value })}
              />
            ) : (
              <ReadonlyTitle value={sub.title} />
            )}
          </Field>
          {isEdit ? (
            <VStack align="stretch" gap="6px">
              <Box
                height="200px"
                sx={invalidEditorSx(Boolean(fieldErrors[articleFieldKey('subsection', sub._id, 'text')]))}
              >
                <MarkdownEditor content={sub.text} setContent={(text) => updateSubsection(sectionId, sub._id, { text })} />
              </Box>
              <TextFooter value={sub.text} error={fieldErrors[articleFieldKey('subsection', sub._id, 'text')]} />
            </VStack>
          ) : (
            <Field error={fieldErrors[articleFieldKey('subsection', sub._id, 'text')]}>
              <ReadonlyText value={sub.text} />
            </Field>
          )}
          <ImagePicker
            existingUrl={sub.imageUrl}
            file={sub.imageFile ?? null}
            onFileChange={(imageFile) => updateSubsection(sectionId, sub._id, { imageFile })}
            fileMinWidthPx={200}
            fileMinHeightPx={200}
          />
        </VStack>
      </Collapse>
    </VStack>
  );

  const renderSection = (section: TArticleSectionDraft, index: number) => {
    // The first section is the lead — always present, no header / remove.
    const isLead = index === 0;
    const open = isLead || hasErrorWithin(section) || !collapsed.has(section._id);

    return (
      <VStack
        key={section._id}
        ref={(el) => {
          blockRefs.current[section._id] = el;
        }}
        align="stretch"
        gap="12px"
      >
        {!isLead && <Divider />}
        {!isLead && (
          <CollapseHeader
            label={`Section ${index}`}
            fontSize="18px"
            isOpen={open}
            onToggle={() => toggle(section._id)}
            removeLabel="Remove section"
            onRemove={() => removeSection(section._id)}
          />
        )}
        <Collapse in={open} animateOpacity>
          <VStack align="stretch" gap="12px">
            <Field error={fieldErrors[articleFieldKey('section', section._id, 'title')]}>
              {isEdit ? (
                <Input
                  {...sectionBoxProps}
                  {...invalidBoxProps(Boolean(fieldErrors[articleFieldKey('section', section._id, 'title')]))}
                  placeholder="Section title"
                  maxLength={CONTENT_TITLE_MAX_LENGTH}
                  value={section.title}
                  height={'80px'}
                  px={'30px'}
                  onChange={(e) => updateSection(section._id, { title: e.target.value })}
                />
              ) : (
                <ReadonlyTitle value={section.title} />
              )}
            </Field>
            {isEdit ? (
              <VStack align="stretch" gap="6px">
                <Box
                  height="220px"
                  sx={invalidEditorSx(Boolean(fieldErrors[articleFieldKey('section', section._id, 'text')]))}
                >
                  <MarkdownEditor content={section.text} setContent={(text) => updateSection(section._id, { text })} />
                </Box>
                <TextFooter value={section.text} error={fieldErrors[articleFieldKey('section', section._id, 'text')]} />
              </VStack>
            ) : (
              <Field error={fieldErrors[articleFieldKey('section', section._id, 'text')]}>
                <ReadonlyText value={section.text} />
              </Field>
            )}
            <ImagePicker
              existingUrl={section.imageUrl}
              file={section.imageFile ?? null}
              onFileChange={(imageFile) => updateSection(section._id, { imageFile })}
              fileMinWidthPx={200}
              fileMinHeightPx={200}
            />

            {section.subsections.map((sub, subIndex) => renderSubsection(section._id, sub, subIndex))}

            <HStack justify="center" py={'10px'}>
              <CustomButton
                label="+ New Sub-Section"
                backgroundColor="extra.white"
                labelColor="text.mediumGray"
                labelHoverColor="text.mediumBlue"
                width="200px"
                onClick={() => addSubsection(section._id)}
              />
            </HStack>
          </VStack>
        </Collapse>
      </VStack>
    );
  };

  return (
    <VStack align="stretch" gap="20px" width="100%" maxWidth="720px">
      <Text fontWeight={500} fontSize="30px" color="text.mediumBlue" textAlign="center" fontFamily="Lora" fontStyle={'italic'}>
        Article Content
      </Text>

      {error && (
        <Text color="extra.red" fontSize="13px" textAlign="center">
          {error}
        </Text>
      )}

      {sections.length === 0 && (
        <Text color="text.mediumGray" fontSize="14px" textAlign="center">
          No sections yet. Add at least one section.
        </Text>
      )}

      {sections.map((section, index) => renderSection(section, index))}

      <Divider />
      <HStack justify="center">
        <CustomButton
          label="+ New Section"
          backgroundColor="extra.white"
          labelColor="text.mediumGray"
          labelHoverColor="text.mediumBlue"
          width="200px"
          onClick={addSection}
        />
      </HStack>
    </VStack>
  );
};

export default ArticleSectionsEditor;
